import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import AdminLayout from '../../components/admin/AdminLayout';
import Avatar from '../../components/common/Avatar';
import { InlineLoader } from '../../components/common/Loader';
import { getAdminPatients } from '../../services/adminService';
import { formatDate } from '../../utils/helpers';

export default function AdminPatients() {
  const [patients, setPatients] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [total, setTotal]       = useState(0);
  const [page, setPage]         = useState(1);
  const [q, setQ]               = useState('');

  const load = useCallback(async (pg = 1) => {
    setLoading(true);
    try {
      const params = { page: pg, limit: 20 };
      if (q) params.q = q;
      const res = await getAdminPatients(params);
      if (pg === 1) setPatients(res.patients || []);
      else setPatients(p => [...p, ...(res.patients || [])]);
      setTotal(res.total || 0);
      setPage(pg);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  }, [q]);

  useEffect(() => { load(1); }, [load]);

  return (
    <AdminLayout title="Patient Management">
      <div className="space-y-4">
        <div className="flex gap-3 items-end">
          <div className="flex-1 max-w-sm">
            <label className="input-label">Search</label>
            <div className="relative">
              <i className="fa-solid fa-magnifying-glass absolute left-3 top-1/2 -translate-y-1/2 text-text-muted text-sm" />
              <input value={q} onChange={e => setQ(e.target.value)}
                placeholder="Name or phone..."
                className="input pl-9" />
            </div>
          </div>
        </div>

        <p className="text-sm text-text-muted">{total} patient{total !== 1 ? 's' : ''}</p>

        {loading && page === 1 ? <InlineLoader text="Loading patients..." /> : (
          <div className="card overflow-hidden">
            <table className="w-full text-left">
              <thead className="bg-surface border-b border-border">
                <tr>
                  <th className="px-4 py-3 text-xs font-semibold text-text-muted uppercase tracking-wide">Patient</th>
                  <th className="px-4 py-3 text-xs font-semibold text-text-muted uppercase tracking-wide">Phone</th>
                  <th className="px-4 py-3 text-xs font-semibold text-text-muted uppercase tracking-wide">Gender / Age</th>
                  <th className="px-4 py-3 text-xs font-semibold text-text-muted uppercase tracking-wide">Location</th>
                  <th className="px-4 py-3 text-xs font-semibold text-text-muted uppercase tracking-wide">Joined</th>
                  <th className="px-4 py-3 text-xs font-semibold text-text-muted uppercase tracking-wide">Status</th>
                </tr>
              </thead>
              <tbody>
                {patients.length === 0 ? (
                  <tr><td colSpan={6} className="text-center py-10 text-sm text-text-muted">No patients found</td></tr>
                ) : patients.map(p => (
                  <tr key={p._id} className="border-b border-border hover:bg-surface">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Avatar name={p.name} size="xs" />
                        <span className="text-sm font-medium text-text-primary">{p.name || 'Not set'}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-text-secondary">{p.phone}</td>
                    <td className="px-4 py-3 text-sm text-text-secondary">
                      {p.gender || '—'}{p.age ? `, ${p.age}y` : ''}
                    </td>
                    <td className="px-4 py-3 text-sm text-text-secondary">
                      {p.location?.city || '—'}
                    </td>
                    <td className="px-4 py-3 text-sm text-text-secondary">
                      {p.createdAt ? formatDate(p.createdAt.split('T')[0]) : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <span className={p.isActive !== false ? 'badge-success' : 'badge-danger'}>
                        {p.isActive !== false ? 'Active' : 'Suspended'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {patients.length < total && (
          <button onClick={() => load(page + 1)} disabled={loading}
            className="w-full py-3 border border-border rounded-btn text-sm font-medium text-primary">
            Load More
          </button>
        )}
      </div>
    </AdminLayout>
  );
}
